import Vue from "vue";
import { mapGetters } from "vuex";
import { LoadingSpinner } from "@/components";
import { themeHelper } from "@/components/mixins/themeHelper";
import { constants } from "@/const";
import DiagramViewer from "@/components/PartDiagramSearch/DiagramViewer/DiagramViewer.vue";
import DiagramPartsList from "@/components/PartDiagramSearch/DiagramPartsList/DiagramPartsList.vue";
import PartDetailRow from "@/components/PartDiagramSearch/PartDetailRow/PartDetailRow.vue";
import PaginationText from "@/components/Elements/PaginationText.vue";
import Pagination from "@/components/Elements/Pagination/Pagination.vue";
import PartSearchFacets from "@/components/SubMenus/PartSearchFacets.vue";
import SkeletonLoader from "@/components/Elements/SkeletonLoader.vue";
import { DnRouterLink } from "@/components/Elements";
import SvgDiagram from "@/components/PartDiagramSearch/PartSearchResultsPanel/SvgDiagram.vue";

/**
 * PartSearchResultsPanel – Renders the right-hand results area of the Part
 * Diagram page. Supports two layout modes:
 *
 *  1. **Diagram mode** (`showDiagram = true`) – side-by-side DiagramViewer
 *     and DiagramPartsList.
 *  2. **List mode** (`showDiagram = false`) – traditional paginated
 *     PartDetailRow list with sort, compact/detailed toggle, and alt-part legend.
 */
export default Vue.extend({
    name: "PartSearchResultsPanel",
    components: {
        LoadingSpinner,
        DiagramViewer,
        DiagramPartsList,
        PartDetailRow,
        PaginationText,
        Pagination,
        PartSearchFacets,
        SkeletonLoader,
        DnRouterLink,
        SvgDiagram,
    },
    mixins: [
        themeHelper
    ],
    props: {
        /** Whether the results are still loading. */
        loading: {
            type: Boolean,
            default: false
        },
        /** Search results object containing `Parts`, `Facets`, etc. */
        searchResults: {
            type: Object,
            default: () => ({})
        },
        /** Whether any returned parts have alternate-part tabs. */
        hasAltParts: {
            type: Boolean,
            default: false
        },
        /** URL (or data-URI) of the diagram image. */
        diagramUrl: {
            type: String,
            default: ""
        },
        /** Alt text for the diagram image. */
        diagramAltText: {
            type: String,
            default: ""
        },
        /** Whether search results contain parts. */
        hasResults: {
            type: Boolean,
            default: false
        },
        /** When true, renders diagram+cards layout instead of list layout. */
        showDiagram: {
            type: Boolean,
            default: true
        },
        /** Total number of parts across all pages. */
        totalParts: {
            type: Number,
            default: 0
        },
        /** Number of parts per page in list mode. */
        partsPerPage: {
            type: Number,
            default: 10
        },
        /** Initial page number (from deep-link or parent). */
        initialPageNumber: {
            type: Number,
            default: 1
        },
        /** Currently selected facets from the parent page. */
        selectedFacets: {
            type: Object,
            default: () => ({})
        },
        /** Active search term text. */
        searchTerm: {
            type: String,
            default: ""
        },
        /** Whether OemManuals mode is active (client-side pagination). */
        isDiagramSearch: {
            type: Boolean,
            default: false
        },
        /** SVG diagram data from OemManuals search. */
        diagramSvgData: {
            type: String,
            default: ""
        },
        /** Whether the diagram for the newly selected component is still loading. */
        diagramLoading: {
            type: Boolean,
            default: false
        },
        /** Title shown above the diagram — the selected component's name. */
        diagramTitle: {
            type: String,
            default: ""
        }
    },
    computed: {
        ...mapGetters([
            "getContentItem",
            "partSearchDefaultSortValue",
            "tmsCompactViewDefaultValue"
        ]),
        /** Key that changes when the active component/diagram changes. */
        diagramContextKey(): string {
            return [
                this.searchResults?.OemDescription || "",
                this.searchResults?.Model || "",
                this.selectedFacets?.Category || "",
                this.selectedFacets?.ComponentGroup || "",
                this.selectedFacets?.Component || "",
                this.diagramSvgData ? `svg:${this.diagramSvgData.length}` : "",
                this.diagramUrl ? `url:${this.diagramUrl.length}` : "",
            ].join("|");
        },
        /** Effective total: in OemManuals mode use the full Parts array length. */
        effectiveTotalParts(): number {
            if (this.isDiagramSearch && this.searchResults?.Parts) {
                return this.searchResults.Parts.length;
            }
            return this.totalParts;
        },
        /** Total pages based on effective total. */
        effectiveTotalPages(): number {
            return Math.ceil(this.effectiveTotalParts / this.partsPerPage);
        },
        /** Parts to display: full array for both OemManuals (non-paginated) and TMS (server-paginated). */
        displayedParts(): any[] {
            return this.searchResults?.Parts || [];
        },
        /** ItemNumbers with a corresponding part, used to grey out unserviceable diagram hotspots. */
        validItemNumbers(): Array<string | number> {
            if (!this.isDiagramSearch || !this.searchResults?.Parts) return [];
            return this.searchResults.Parts
                .map((p: any) => p.ItemNumber)
                .filter((n: any) => n != null);
        },
        /** True only when an actual diagram (SVG or image) was found for the current search. */
        hasDiagram(): boolean {
            return !!(this.diagramSvgData);
        },
    },
    data: () => ({
        /** Whether the condensed (compact) part view is active. */
        compactView: false,
        pageNumber: 1,
        selectedSortTerm: "",
        sortByOptions: constants.SORT_BY_OPTIONS,
        inStockFilter: false,
        /** Whether the sticky diagram is expanded. */
        diagramExpanded: true,
        /** Current zoom level for the sticky diagram. */
        diagramZoom: 0.5,
        /** Last manual viewport height used before the diagram was hidden. */
        savedDiagramViewportHeight: "",
        /** ItemNumber of the part currently highlighted via a diagram hotspot click. */
        highlightedItemNumber: null as string | number | null,
    }),
    created() {
        this.pageNumber = this.initialPageNumber || 1;
        this.compactView = this.tmsCompactViewDefaultValue === "true" || this.tmsCompactViewDefaultValue === true;

        // NOTE: The parent (PartDiagramPage) triggers the initial search via
        // getCategoryParts / loadOemManualsDiagram in onSearchComplete, so we
        // intentionally do NOT call performPartSearch here to avoid a duplicate
        // API call on first render.
    },
    watch: {
        /** Persist compact-view preference whenever the user toggles it. */
        compactView(newVal: boolean) {
            this.$store.commit("SET_TMS_COMPACT_VIEW", newVal);
        },
        /** Sync page number when the parent signals a new page (new search or pagination). */
        initialPageNumber(newVal: number) {
            this.pageNumber = newVal || 1;
        },
        /** Reset zoom and pan whenever the active diagram context changes. */
        diagramContextKey() {
            this.resetDiagramView();
        }
    },
    methods: {
        /** Bubble the hasAltParts event to the parent page. */
        HasAltParts(emittedHasAlts: boolean) {
            this.$emit("hasAltParts", emittedHasAlts);
        },

        /** Forward the facet-update event to the parent page. */
        UpdateFacets() {
            this.$emit("updateFacets");
        },

        /** Forward the redirect-to-truck-select event to the parent page. */
        redirectToTruckSelect() {
            this.$emit("redirectToTruckSelect");
        },

        /** Triggers a new part search for the given page. In OemManuals mode,
         *  pagination is client-side so we just update the page number.
         *  In TMS mode, emits to the parent for a server-side fetch. */
        performPartSearch(page: number) {
            this.pageNumber = page;
            if (this.isDiagramSearch) {
                // Client-side pagination — displayedParts computed will react
                return;
            }
            if (this.selectedSortTerm === "") {
                this.selectedSortTerm = this.partSearchDefaultSortValue === "" ? "salesVolumeDesc" : this.partSearchDefaultSortValue;
            }
            const selectedSortOption = this.sortByOptions.find((option: any) => option.value === this.selectedSortTerm);
            this.$emit("partSearch", selectedSortOption?.sortKey, selectedSortOption?.sortDirection, this.pageNumber, this.inStockFilter);
        },

        /** Builds a URL with the given category set as a query parameter. */
        getCategoryUrl(category: string): string {
            const url = new URL(window.location.href);
            url.searchParams.set("Category", category);
            return url.pathname + url.search;
        },

        /** Handles mouse wheel zoom on the diagram. */
        handleDiagramWheel(event: WheelEvent) {
            event.preventDefault();

            // Use Ctrl+Scroll or just scroll to zoom
            const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
            this.zoomDiagramBy(zoomDelta, event.clientX, event.clientY);
        },

        /** Zooms the diagram around the given point, or the viewport center when no point is supplied. */
        zoomDiagramBy(delta: number, clientX?: number, clientY?: number) {
            const nextZoom = Math.max(0.5, Math.min(3, this.diagramZoom + delta));
            const svgDiagram = this.$refs.svgDiagram as any;
            const diagramContent = this.$refs.diagramContent as HTMLElement | undefined;

            if (svgDiagram?.zoomToPoint) {
                const viewportRect = diagramContent?.getBoundingClientRect();
                const targetX = clientX ?? (viewportRect ? viewportRect.left + viewportRect.width / 2 : 0);
                const targetY = clientY ?? (viewportRect ? viewportRect.top + viewportRect.height / 2 : 0);
                svgDiagram.zoomToPoint(nextZoom, targetX, targetY);
            }

            this.diagramZoom = nextZoom;
        },

        /** Resets zoom, recenters the diagram, and clears any inline width/height the native resize handle applied. */
        resetZoomAndSize() {
            this.resetDiagramView();
            const el = this.$refs.diagramContent as HTMLElement | undefined;
            if (el) {
                el.style.width = "";
                el.style.height = "";
            }
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (wrapper) {
                wrapper.style.height = "";
            }
        },

        /** Stores the last visible height so it can be restored after the diagram is hidden. */
        saveDiagramViewportHeight() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (!wrapper) return;

            const height = wrapper.getBoundingClientRect().height;
            if (height > 0) {
                this.savedDiagramViewportHeight = `${Math.round(height)}px`;
            }
        },

        /** Restores the last saved height for the diagram viewport when it is shown again. */
        restoreDiagramViewportHeight() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            if (!wrapper || !this.savedDiagramViewportHeight) return;

            wrapper.style.height = this.savedDiagramViewportHeight;
            wrapper.style.minHeight = "";
        },

        /** Toggles the diagram visibility while preserving the last viewport height for restore. */
        toggleDiagramExpanded() {
            const wrapper = (this.$el as HTMLElement | null)?.querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;

            if (this.diagramExpanded && wrapper) {
                this.saveDiagramViewportHeight();
            }

            this.diagramExpanded = !this.diagramExpanded;

            if (!this.diagramExpanded) {
                if (wrapper) {
                    wrapper.style.height = "";
                    wrapper.style.minHeight = "0";
                }

                const content = this.$refs.diagramContent as HTMLElement | undefined;
                if (content) {
                    content.style.height = "";
                }
                return;
            }

            this.restoreDiagramViewportHeight();
        },

        /** Resets zoom and pan to the default centered view. */
        resetDiagramView() {
            this.diagramZoom = 0.5;
            this.highlightedItemNumber = null;
            this.$nextTick(() => {
                const svgDiagram = this.$refs.svgDiagram as any;
                if (svgDiagram?.resetPan) {
                    svgDiagram.resetPan();
                }
            });
        },

        /**
         * Handles a click on a diagram hotspot (SvgDiagram emits the hotspot's
         * `name` attribute, which matches a part's ItemNumber). Highlights the
         * matching part row and scrolls it into view.
         */
        onHotspotClick(itemNumber: string) {
            this.highlightedItemNumber = itemNumber;
            this.$nextTick(() => {
                this.scrollPartRowIntoView(itemNumber);
            });
        },

        /**
         * Scrolls the given part row into view, offsetting for the sticky
         * diagram's height so the row (and its FullPartDetails) aren't left
         * hidden underneath it.
         */
        scrollPartRowIntoView(itemNumber: string | number) {
            const rowEl = document.getElementById(this.partRowId(itemNumber));
            if (!rowEl) return;

            const stickyEl = (this.$el as HTMLElement).querySelector(".diagram-results-panel__sticky-diagram-wrapper") as HTMLElement | null;
            const stickyHeight = stickyEl ? stickyEl.getBoundingClientRect().height : 0;

            const rowTop = rowEl.getBoundingClientRect().top + window.scrollY;
            const breathingRoom = 16;
            window.scrollTo({ top: rowTop - stickyHeight - breathingRoom, behavior: "smooth" });
        },

        /** Builds the DOM id used to locate/scroll to a part row by ItemNumber. */
        partRowId(itemNumber: string | number): string {
            return `diagram-part-row-${itemNumber}`;
        },

        /** True when the given part's ItemNumber matches the currently highlighted hotspot. */
        isPartHighlighted(part: any): boolean {
            return (
                this.highlightedItemNumber != null &&
                part?.ItemNumber != null &&
                String(part.ItemNumber) === String(this.highlightedItemNumber)
            );
        },

        /**
         * Handles a click on a part's ItemNumber badge: expands/zooms the diagram
         * and pans+highlights the matching hotspot.
         */
        async onItemNumberClick(itemNumber: string | number) {
            this.highlightedItemNumber = itemNumber;
            this.diagramExpanded = true;
            this.diagramZoom = Math.max(this.diagramZoom, 1.7);

            await this.$nextTick();

            const svgDiagram = this.$refs.svgDiagram as any;
            if (svgDiagram?.focusHotspot) {
                await svgDiagram.focusHotspot(itemNumber);
            }

            const diagramWrapper = (this.$el as HTMLElement).querySelector(".diagram-results-panel__sticky-diagram-wrapper");
            if (diagramWrapper) {
                diagramWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }
});
